import { ComponentType, ReactNode } from 'react';
import IconBook from '../components/Icon/IconBook';
import IconChartSquare from '../components/Icon/IconChartSquare';
import IconCreditCard from '../components/Icon/IconCreditCard';
import IconGift from '../components/Icon/IconGift';
import IconLink from '../components/Icon/IconLink';
import IconMenuDashboard from '../components/Icon/Menu/IconMenuDashboard';
import IconMenuDocumentation from '../components/Icon/Menu/IconMenuDocumentation';
import IconMenuMailbox from '../components/Icon/Menu/IconMenuMailbox';
import IconMenuNotes from '../components/Icon/Menu/IconMenuNotes';
import IconPrinter from '../components/Icon/IconPrinter';
import IconSettings from '../components/Icon/IconSettings';
import IconTrendingUp from '../components/Icon/IconTrendingUp';
import IconUsersGroup from '../components/Icon/IconUsersGroup';
import MonthTabs from '../components/Layouts/MonthTabs';

type Gated = {
    /**
     * Available in basic app mode. This is the single place a route is characterized:
     * it drives both sidebar visibility AND the router allowlist
     * (see BASIC_MODE_ALLOWED_PATHS in config/appMode.ts).
     */
    basic?: boolean;
    /** Available in full mode. @default true*/
    full?: boolean;
};

export type NavSection = Gated & {
    kind: 'section';
    labelKey: string;
    defaultLabel?: string;
};

export type NavLeaf = Gated & {
    path: string;
    labelKey: string;
    defaultLabel?: string;
};

export type NavLinkItem = Gated & NavLeaf & {
    kind: 'link';
    icon: ComponentType<any>;
    /** Extra props forwarded to the icon component (e.g. { duotone: true, fill: true }). */
    iconProps?: Record<string, unknown>;
    /** 'promo' renders the highlighted call-to-action styling (Offers Hub). */
    variant?: 'default' | 'promo';
};

export type NavGroup = Gated & {
    kind: 'group';
    /** Accordion key — must be unique. */
    id: string;
    labelKey: string;
    defaultLabel?: string;
    icon: ComponentType<any>;
    children?: NavLeaf[];
    /** Escape hatch: render arbitrary content inside the accordion instead of child links. */
    body?: () => ReactNode;
};

export type NavEntry = NavSection | NavLinkItem | NavGroup;

export const SIDEBAR_CONFIG: NavEntry[] = [
    {
        kind: 'group',
        id: 'dashboard',
        labelKey: 'dashboard',
        icon: IconMenuDashboard,
        children: [
            { path: '/dashboard/overview', labelKey: 'overview.title' },
            { path: '/dashboard/annual-total', labelKey: 'annual_total' },
            { path: '/dashboard/calendar', labelKey: 'calendar' },
        ],
    },

    // duplicate link
    { kind: 'link', path: '/dashboard/overview', labelKey: 'overview.title', icon: IconMenuDashboard, full: false, basic: true },

    { kind: 'section', labelKey: 'budgets_planning' },
    { kind: 'link', path: '/dashboard/budget-manager', labelKey: 'budget_manager.title', defaultLabel: 'Budget Manager', icon: IconChartSquare },
    { kind: 'link', path: '/dashboard/build-your-wealth/conscious-spending-plan', labelKey: 'conscious_spending_plan', icon: IconMenuDocumentation },
    { kind: 'link', path: '/dashboard/build-your-wealth/sinking-funds', labelKey: 'sinking_funds', icon: IconMenuDocumentation },
    { kind: 'link', path: '/dashboard/build-your-wealth/recommendations', labelKey: 'offers_hub.page_title', icon: IconGift, variant: 'promo' },

    { kind: 'section', labelKey: 'tracking', basic: true },
    {
        kind: 'group',
        id: 'transactions',
        labelKey: 'transactions',
        icon: IconMenuMailbox,
        children: [
            { path: '/dashboard/transactions/recurring', labelKey: 'recurring_transactions' },
            { path: '/dashboard/transactions/payments', labelKey: 'payments' },
            { path: '/dashboard/transactions/variables', labelKey: 'variable_transactions' },
        ],
    },

    // duplicate link
    { kind: 'link', path: '/dashboard/transactions/variables', labelKey: 'variable_transactions', icon: IconMenuMailbox, full: false, basic: true },

    { kind: 'link', path: '/dashboard/accounts', labelKey: 'accounts', icon: IconCreditCard, basic: true },
    { kind: 'link', path: '/dashboard/investments', labelKey: 'investments', icon: IconTrendingUp },

    {
        kind: 'group',
        id: 'tools',
        labelKey: 'tools',
        icon: IconMenuNotes,
        children: [
            { path: '/dashboard/reports/cash-flow', labelKey: 'reports.cash_flow_forecast', defaultLabel: 'Cash Flow Forecast' },
            { path: '/dashboard/tools/subscription-tracker', labelKey: 'subscription_tracker' },
            { path: '/dashboard/tools/emotional-spending', labelKey: 'emotional_spending' },
            { path: '/dashboard/tools/buffer-budget', labelKey: 'buffer_budget' },
            { path: '/dashboard/tools/informal-debt', labelKey: 'informal_debt' },
            { path: '/dashboard/tools/goal-prioritization-matrix', labelKey: 'goal_prioritization_matrix' },
            { path: '/dashboard/tools/habit-tracker', labelKey: 'financial_habit_tracker' },
            { path: '/dashboard/tools/legacy-vault', labelKey: 'legacy_vault' },
            { path: '/dashboard/tools/debt-payoff-calculator', labelKey: 'reports.debt_payoff_calculator' },
            { path: '/dashboard/build-your-wealth/affordability-calculator', labelKey: 'affordability_calculator' },
        ],
    },
    {
        kind: 'group',
        id: 'reports',
        labelKey: 'reports.title',
        defaultLabel: 'Reports',
        icon: IconPrinter,
        children: [
            { path: '/dashboard/reports/lifestyle-creep', labelKey: 'reports.lifestyle_creep', defaultLabel: 'Lifestyle Creep' },
            { path: '/dashboard/reports/emotional-spending', labelKey: 'reports.emotional_spending_analysis', defaultLabel: 'Emotional Spending Analysis' },
            { path: '/dashboard/reports/net-worth', labelKey: 'reports.net_worth', defaultLabel: 'Net Worth Report' },
            { path: '/dashboard/reports/budget-performance', labelKey: 'budget_performance', defaultLabel: 'Budget Performance' },
            { path: '/dashboard/reports/spending-comparison', labelKey: 'reports.spending_comparison', defaultLabel: 'Spending Comparison' },
        ],
    },

    { kind: 'section', labelKey: 'integrations' },
    { kind: 'link', path: '/dashboard/integrations/boad-finance', labelKey: 'boad_finance.sidebar_label', icon: IconLink },

    { kind: 'section', labelKey: 'time_period' },
    {
        kind: 'group',
        id: 'time_period',
        labelKey: 'select_period',
        defaultLabel: 'Select Period',
        icon: IconMenuDashboard,
        body: () => <MonthTabs />,
    },

    { kind: 'section', labelKey: 'user', basic: true },
    { kind: 'link', path: '/dashboard/users/account-settings', labelKey: 'account_settings', icon: IconSettings, iconProps: { duotone: true, fill: true }, basic: true },
    { kind: 'link', path: '/dashboard/users/household-settings', labelKey: 'household_settings', icon: IconUsersGroup },
    { kind: 'link', path: '/dashboard/users/subscription-plans', labelKey: 'subscription_plans', icon: IconMenuDocumentation, basic: true },
    { kind: 'link', path: '/dashboard/app-guide', labelKey: 'app_guide', icon: IconBook },
];

export type NavVisibilityContext = {
    isBasicMode: boolean;
};

function allowedInMode(entry: Gated, ctx: NavVisibilityContext): boolean {
    if (ctx.isBasicMode) {
        return !!entry.basic;
    }

    return entry.full ?? true;
}

/** Children of a group that survive the current app-mode gating. */
export function visibleChildrenOf(group: NavGroup, ctx: NavVisibilityContext): NavLeaf[] {
    return (group.children || []).filter(child => allowedInMode(child, ctx));
}

/**
 * A link/section is visible when it's allowed in the current mode.
 * A group with children disappears once all its children are hidden (so no empty accordions);
 * a body-group (e.g. the period picker) is gated by its own flag instead.
 */
export function isEntryVisible(entry: NavEntry, ctx: NavVisibilityContext): boolean {
    if (entry.kind === 'group' && !entry.body) return visibleChildrenOf(entry, ctx).length > 0;
    return allowedInMode(entry, ctx);
}

/** Every path in the nav marked as available in basic mode. */
export function collectBasicNavPaths(entries: NavEntry[] = SIDEBAR_CONFIG): string[] {
    const paths: string[] = [];
    entries.forEach(entry => {
        if (entry.kind === 'link' && entry.basic) paths.push(entry.path);
        if (entry.kind === 'group') {
            entry.children?.forEach(child => {
                if (child.basic) paths.push(child.path);
            });
        }
    });
    return paths;
}
